import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Volume2, VolumeX, Play, Loader2 } from "lucide-react";

/**
 * Voice narrator for 3D scenes. Uses /api/ai/tts.
 * - Auto-plays the script on mount (unless muted).
 * - "Play again" button re-plays on demand.
 * - Reads TTS voice from localStorage("brimi_tts_voice") for per-user pref.
 * - Mute toggle persisted in localStorage("brimi_narrator_muted").
 */
export const Narrator = ({ script, sceneKey }) => {
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(() => localStorage.getItem("brimi_narrator_muted") === "1");
  const audioRef = useRef(null);
  const urlRef = useRef(null);
  const sessionRef = useRef(0);
  const mountedRef = useRef(true);

  const stop = () => {
    sessionRef.current += 1; // discard any in-flight fetch
    try { audioRef.current?.pause(); } catch (_e) { /* ignore */ }
    if (audioRef.current) { audioRef.current.onended = null; audioRef.current.onerror = null; audioRef.current.src = ""; }
    setPlaying(false);
    cleanupUrl();
    audioRef.current = null;
  };

  const play = async () => {
    if (!script || busy) return;
    stop();
    const mySession = sessionRef.current;
    setBusy(true);
    try {
      const voice = localStorage.getItem("brimi_tts_voice") || "shimmer";
      const clean = script.replace(/\s+/g, " ").slice(0, 3800);
      const res = await api.post("/ai/tts", { text: clean, voice }, { responseType: "blob" });
      if (!mountedRef.current || sessionRef.current !== mySession) return;
      const url = URL.createObjectURL(res.data);
      urlRef.current = url;
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onplay = () => { if (mountedRef.current && sessionRef.current === mySession) setPlaying(true); };
      audio.onended = () => { if (mountedRef.current) { setPlaying(false); cleanupUrl(); } };
      audio.onerror = () => { if (mountedRef.current) { setPlaying(false); cleanupUrl(); } };
      await audio.play();
    } catch (err) {
      console.warn("Narrator TTS failed:", err?.message || err);
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  };

  const cleanupUrl = () => {
    if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
  };

  const toggleMute = () => {
    setMuted((m) => {
      const next = !m;
      localStorage.setItem("brimi_narrator_muted", next ? "1" : "0");
      if (next) stop();
      return next;
    });
  };

  // Auto-play once when scene mounts / changes (respect mute)
  useEffect(() => {
    mountedRef.current = true;
    if (muted) return;
    const t = setTimeout(() => play(), 400);
    return () => { clearTimeout(t); mountedRef.current = false; stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneKey]);

  return (
    <div className="absolute top-3 right-3 z-30 flex items-center gap-2 bg-slate-900/80 backdrop-blur-md border border-cyan-500/30 rounded-full pl-3 pr-1 py-1 shadow-lg">
      <span className="text-[10px] uppercase tracking-widest text-cyan-300 font-mono">
        {playing ? "Brimi narrates…" : "Voice narration"}
      </span>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        onClick={play}
        disabled={busy || muted}
        className="h-7 w-7 rounded-full hover:bg-cyan-500/20"
        data-testid="narrator-play"
        aria-label="Play narration"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin text-cyan-300" /> : <Play className="h-3.5 w-3.5 text-cyan-300" />}
      </Button>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        onClick={toggleMute}
        className="h-7 w-7 rounded-full hover:bg-cyan-500/20"
        data-testid="narrator-mute"
        aria-label={muted ? "Unmute narrator" : "Mute narrator"}
      >
        {muted ? <VolumeX className="h-3.5 w-3.5 text-slate-500" /> : <Volume2 className="h-3.5 w-3.5 text-cyan-300" />}
      </Button>
    </div>
  );
};

export default Narrator;
